"use client";

import { useState } from "react";
import {
    CircleHelp,
    Plus,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useLanguage } from "@/components/LanguageContext";

const faqContentHu = {
    pageTitle: (
        <>
            gyakran ismételt
            <br />
            kérdések
        </>
    ),

    introduction: (
        <>
            minden fontos információ
            <br className="md:hidden" />{" "}
            egy helyen, amit érdemes
            <br className="md:hidden" />{" "}
            tudnod érkezés előtt.
        </>
    ),

    items: [
        {
            question:
                "mikor kell megérkezni?",

            answer:
                "a lefoglalt időpont előtt legalább 15 perccel meg kell érkezni. a játék pontosan, a meghirdetett időpontban indul.",

            important:
                true,
        },
        {
            question:
                "mennyi ideig tart egy játék?",

            answer:
                "egy turnus 60 percig tart.",
        },
        {
            question:
                "hányan játszhatnak egyszerre?",

            answer:
                "egy pályán egyszerre több játékos is részt vehet, a választott játékmódtól függően.",
        },
        {
            question:
                "kell előre időpontot foglalni?",

            answer:
                "igen, javasoljuk az előzetes online időpontfoglalást.",
        },
        {
            question:
                "lehet helyszínen is jegyet vásárolni?",

            answer:
                "igen, amennyiben van még szabad hely az adott időpontra.",
        },
        {
            question:
                "módosíthatom a lefoglalt időpontomat?",

            answer:
                "igen. a lefoglalt időpont legkésőbb 24 órával a játék kezdete előtt díjmentesen módosítható, a szabad helyek függvényében. 24 órán belül az időpont már nem módosítható.",
        },
        {
            question:
                "lemondhatom a foglalásomat?",

            answer:
                "a megvásárolt jegy ára lemondás esetén nem téríthető vissza. az időpont legkésőbb 24 órával a játék kezdete előtt módosítható.",
        },
        {
            question:
                "átadhatom másnak a jegyemet?",

            answer:
                "igen. a megvásárolt jegy más személy részére átadható.",
        },
        {
            question:
                "hogyan kapom meg a jegyemet?",

            answer:
                "sikeres online vásárlás után a belépéshez szükséges elektronikus jegyet és QR-kódot a vásárláskor megadott e-mail-címre küldjük.",
        },
        {
            question:
                "hogyan tudok fizetni?",

            answer:
                "online bankkártyával, a helyszínen pedig bankkártyával vagy készpénzzel lehet fizetni.",
        },
        {
            question:
                "lehet alkohol fogyasztása után játszani?",

            answer:
                "alkohol, kábítószer vagy más, a biztonságos játékot befolyásoló szer hatása alatt a játékban nem lehet részt venni.",
        },
        {
            question:
                "van ruhatár?",

            answer:
                "igen, a strike ruhatárat biztosít a vendégek részére.",
        },
        {
            question:
                "gyerekek is játszhatnak?",

            answer:
                "igen. a játékban 10 éves kortól lehet részt venni. 10 és 13 éves kor között szülői vagy felnőtt kíséret szükséges, 14 éves kortól pedig kíséret nélkül is lehet játszani.",
        },
        {
            question:
                "lehet késni?",

            answer:
                "nem. a játék pontosan, a lefoglalt időpontban indul. kérjük, legalább 15 perccel korábban érkezzetek. aki a játék kezdetéig nem érkezik meg, a már megkezdett játékhoz nem tud csatlakozni.",
        },
        {
            question:
                "behozhatok saját ételt vagy italt?",

            answer:
                "nem. élelmiszerbiztonsági előírások miatt kívülről hozott ételt és italt nem lehet behozni vagy a helyszínen elfogyasztani.",
        },
        {
            question:
                "mi az a Feed the Beast?",

            answer:
                "a Feed the Beast egy bónuszjáték, amelyet a strike zone pályákon elért 1500 pont után lehet megnyitni.",
        },
        {
            question:
                "van parkolási lehetőség?",

            answer:
                "igen, a strike közelében több parkolási lehetőség is rendelkezésre áll.",
        },
        {
            question:
                "kell hozzá ügyesnek lennem?",

            answer:
                "nem. a játékokat bárki élvezheti, a kezdőktől a tapasztalt játékosokig.",
        },
    ],
};

const faqContentEn = {
    pageTitle: (
        <>
            frequently asked
            <br />
            questions
        </>
    ),

    introduction: (
        <>
            everything you need to know
            <br className="md:hidden" />{" "}
            in one place before
            <br className="md:hidden" />{" "}
            you arrive.
        </>
    ),

    items: [
        {
            question:
                "when should I arrive?",

            answer:
                "you must arrive at least 15 minutes before your booked time. the game starts exactly at the scheduled time.",

            important:
                true,
        },
        {
            question:
                "how long does a game last?",

            answer:
                "one game session lasts 60 minutes.",
        },
        {
            question:
                "how many people can play at the same time?",

            answer:
                "multiple players can play on the same course at the same time, depending on the selected game mode.",
        },
        {
            question:
                "do I need to book in advance?",

            answer:
                "yes, we recommend booking your time slot online in advance.",
        },
        {
            question:
                "can I buy tickets at the venue?",

            answer:
                "yes, provided there are still available places for the selected time slot.",
        },
        {
            question:
                "can I change my booking?",

            answer:
                "yes. your booking can be changed free of charge up to 24 hours before the start of the game, subject to availability. bookings can no longer be changed within 24 hours of the scheduled start time.",
        },
        {
            question:
                "can I cancel my booking?",

            answer:
                "tickets are non-refundable in the event of cancellation. the booking time can be changed up to 24 hours before the start of the game.",
        },
        {
            question:
                "can I give my ticket to someone else?",

            answer:
                "yes. purchased tickets may be transferred to another person.",
        },
        {
            question:
                "how will I receive my ticket?",

            answer:
                "after a successful online purchase, the electronic ticket and QR code required for entry will be sent to the e-mail address provided during the purchase.",
        },
        {
            question:
                "how can I pay?",

            answer:
                "online payments can be made by bank card. at the venue, you can pay by bank card or cash.",
        },
        {
            question:
                "can I play after drinking alcohol?",

            answer:
                "you may not participate in the game while under the influence of alcohol, drugs or any other substance that may affect your ability to play safely.",
        },
        {
            question:
                "is there a cloakroom?",

            answer:
                "yes, strike provides a cloakroom for guests.",
        },
        {
            question:
                "can children play?",

            answer:
                "yes. players must be at least 10 years old. children aged 10 to 13 must be accompanied by a parent or another adult. from the age of 14, players may participate without adult supervision.",
        },
        {
            question:
                "can I be late?",

            answer:
                "no. the game starts exactly at the booked time. please arrive at least 15 minutes early. anyone who has not arrived by the start of the game cannot join once the game has begun.",
        },
        {
            question:
                "can I bring my own food or drinks?",

            answer:
                "no. due to food safety regulations, food and drinks brought from outside may not be brought into or consumed at the venue.",
        },
        {
            question:
                "what is Feed the Beast?",

            answer:
                "Feed the Beast is a bonus game that can be unlocked after reaching 1500 points on the strike zone courses.",
        },
        {
            question:
                "is parking available?",

            answer:
                "yes, several parking options are available near strike.",
        },
        {
            question:
                "do I need to be good at it?",

            answer:
                "no. the games are designed to be enjoyed by everyone, from complete beginners to experienced players.",
        },
    ],
};

export default function GyikPage() {
    const {
        language,
    } =
        useLanguage();

    const [
        openIndex,
        setOpenIndex,
    ] =
        useState(
            0
        );

    const content =
        language === "en"
            ? faqContentEn
            : faqContentHu;

    const toggleItem =
        (index) => {
            setOpenIndex(
                (
                    currentIndex
                ) =>
                    currentIndex ===
                        index
                        ? null
                        : index
            );
        };

    return (
        <main className="min-h-screen overflow-x-hidden bg-black font-sans">
            <Header />

            <section className="bg-[#dedede] text-black">
                {/* OLDAL TETEJE */}
                <div className="mx-auto flex w-full max-w-[430px] flex-col items-center px-7 pb-12 pt-12 text-center md:max-w-5xl md:px-12 md:pb-16 md:pt-16">
                    <div
                        className="mb-6 flex shrink-0 items-center justify-center rounded-full border-[3px] border-black"
                        style={{
                            width:
                                "80px",

                            height:
                                "80px",

                            minWidth:
                                "80px",

                            maxWidth:
                                "80px",

                            minHeight:
                                "80px",

                            maxHeight:
                                "80px",

                            boxSizing:
                                "border-box",
                        }}
                    >
                        <CircleHelp
                            style={{
                                width:
                                    "42px",

                                height:
                                    "42px",
                            }}
                            strokeWidth={
                                2.5
                            }
                        />
                    </div>

                    <h1 className="text-[25px] font-black lowercase leading-none md:text-[48px]">
                        {
                            content.pageTitle
                        }
                    </h1>

                    <p className="mt-8 max-w-[310px] text-[15px] leading-[1.15] text-neutral-700 md:max-w-xl md:text-[21px] md:leading-[1.35]">
                        {
                            content.introduction
                        }
                    </p>
                </div>

                {/* HARMONIKA */}
                <div className="mx-auto w-full max-w-[430px] px-5 pb-20 md:max-w-4xl md:px-10 md:pb-32">
                    <div className="overflow-hidden border-[3px] border-black bg-black">
                        {content.items.map(
                            (
                                item,
                                index
                            ) => {
                                const isOpen =
                                    openIndex ===
                                    index;

                                const itemNumber =
                                    String(
                                        index +
                                        1
                                    ).padStart(
                                        2,
                                        "0"
                                    );

                                return (
                                    <div
                                        key={
                                            item.question
                                        }
                                        className={`${index !==
                                                content
                                                    .items
                                                    .length -
                                                1
                                                ? "border-b-[3px] border-black"
                                                : ""
                                            }`}
                                    >
                                        <button
                                            type="button"
                                            onClick={() =>
                                                toggleItem(
                                                    index
                                                )
                                            }
                                            aria-expanded={
                                                isOpen
                                            }
                                            aria-controls={`faq-answer-${index}`}
                                            className={`flex min-h-[74px] w-full cursor-pointer items-center gap-4 px-5 py-5 text-left transition-colors duration-300 md:min-h-[94px] md:gap-6 md:px-8 md:py-6 ${isOpen
                                                    ? "bg-black text-white"
                                                    : item.important
                                                        ? "bg-[#f5a400] text-black hover:bg-[#ffb21a]"
                                                        : "bg-[#f2f2f2] text-black hover:bg-white"
                                                }`}
                                        >
                                            <span
                                                className={`shrink-0 text-[11px] font-black tracking-[0.12em] md:text-[13px] ${isOpen
                                                        ? "text-white/50"
                                                        : "text-neutral-400"
                                                    }`}
                                            >
                                                {
                                                    itemNumber
                                                }
                                            </span>

                                            <span className="flex-1">
                                                {item.important && (
                                                    <span
                                                        className={`mb-1.5 block text-[9px] font-black uppercase tracking-[0.22em] md:text-[11px] ${isOpen
                                                                ? "text-[#f5a400]"
                                                                : "text-black/60"
                                                            }`}
                                                    >
                                                        {language === "en"
                                                            ? "important"
                                                            : "fontos"}
                                                    </span>
                                                )}

                                                <span className="block text-[16px] font-black lowercase leading-[1.15] md:text-[22px]">
                                                    {
                                                        item.question
                                                    }
                                                </span>
                                            </span>

                                            <span
                                                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 transition-all duration-300 md:h-11 md:w-11 ${isOpen
                                                        ? "rotate-45 border-white bg-white text-black"
                                                        : "border-black bg-transparent text-black"
                                                    }`}
                                                aria-hidden="true"
                                            >
                                                <Plus
                                                    className="h-5 w-5 md:h-6 md:w-6"
                                                    strokeWidth={
                                                        2.5
                                                    }
                                                />
                                            </span>
                                        </button>

                                        <div
                                            id={`faq-answer-${index}`}
                                            className={`grid bg-[#cfcfcf] transition-[grid-template-rows] duration-300 ease-in-out ${isOpen
                                                    ? "grid-rows-[1fr]"
                                                    : "grid-rows-[0fr]"
                                                }`}
                                        >
                                            <div className="overflow-hidden">
                                                <div className="px-5 pb-7 pt-6 md:px-8 md:pb-9 md:pt-8">
                                                    <div className="flex gap-4 md:gap-6">
                                                        <div className="w-[22px] shrink-0 md:w-[27px]" />

                                                        {item.important ? (
                                                            <div className="max-w-2xl flex-1">
                                                                <div className="mb-5 flex items-center gap-4 md:gap-6">
                                                                    <div className="flex h-[72px] w-[72px] shrink-0 rotate-[-4deg] flex-col items-center justify-center border-[3px] border-black bg-[#f5a400] shadow-[4px_4px_0_0_#000000] md:h-[90px] md:w-[90px]">
                                                                        <strong className="text-[31px] font-black leading-none md:text-[40px]">
                                                                            15
                                                                        </strong>

                                                                        <span className="mt-1 text-[9px] font-black uppercase tracking-[0.12em] md:text-[11px]">
                                                                            {language === "en"
                                                                                ? "minutes"
                                                                                : "perccel"}
                                                                        </span>
                                                                    </div>

                                                                    <p className="text-[17px] font-black uppercase leading-[1.1] text-black md:text-[23px]">
                                                                        {language === "en"
                                                                            ? "arrive earlier!"
                                                                            : "érkezzetek korábban!"}
                                                                    </p>
                                                                </div>

                                                                <p className="text-[15px] leading-[1.35] text-neutral-700 md:text-[20px] md:leading-[1.45]">
                                                                    {
                                                                        item.answer
                                                                    }
                                                                </p>
                                                            </div>
                                                        ) : (
                                                            <p className="max-w-2xl text-[15px] leading-[1.35] text-neutral-700 md:text-[20px] md:leading-[1.45]">
                                                                {
                                                                    item.answer
                                                                }
                                                            </p>
                                                        )}

                                                        <div className="h-9 w-9 shrink-0 md:h-11 md:w-11" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            }
                        )}
                    </div>
                </div>
            </section>

            <Footer />
        </main>
    );
}